import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import erpInventoryServices from "app/services/erp-inventory-services";

// Sugerencia automática de posiciones por rotación.
//
// El criterio es el slotting ABC de cualquier WMS: lo que más se pickea va lo
// más cerca del inicio del recorrido, que es donde está el despacho. Cada visita
// al rack que se acorta se multiplica por las veces que se hace.
//
// Se muestra la propuesta antes de aplicar. Un botón que reorganiza la bodega en
// silencio sería más "automático", pero nadie entendería después por qué el
// picking cambió.
const SlottingDialog = ({ open, onClose, centres, onApplied }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [centreId, setCentreId] = useState("");
    const [includeStale, setIncludeStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setResult(null);
        setCentreId(centres.length === 1 ? centres[0].id : "");
    }, [open, centres]);

    const load = useCallback(async () => {
        if (!centreId) return;

        try {
            setLoading(true);
            const params = `?centre_id=${centreId}&include_stale=${includeStale}`;
            setResult(await erpInventoryServices.getSlottingSuggestions(params));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudieron calcular", { variant: "error" });
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [centreId, includeStale, enqueueSnackbar]);

    useEffect(() => { load(); }, [load]);

    const apply = async () => {
        try {
            setApplying(true);
            const response = await erpInventoryServices.applySlotting({
                centre_id: centreId,
                include_stale: includeStale,
            });
            enqueueSnackbar(response.message || "Posiciones asignadas", { variant: "success" });
            onApplied();
            onClose();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo aplicar", { variant: "error" });
        } finally {
            setApplying(false);
        }
    };

    const suggestions = result?.suggestions || [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Sugerir posiciones por rotación
                <Typography variant="body2" color="text.secondary">
                    Lo que más se pickea queda más cerca del despacho.
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <TextField select size="small" label="Bodega" value={centreId} sx={{ minWidth: 200 }}
                               onChange={(event) => setCentreId(event.target.value)}>
                        {centres.map((centre) => (
                            <MenuItem key={centre.id} value={centre.id}>{centre.name}</MenuItem>
                        ))}
                    </TextField>

                    <FormControlLabel
                        control={<Switch checked={includeStale} onChange={(e) => setIncludeStale(e.target.checked)} />}
                        label="Incluir lo que no se mueve"
                    />
                </Stack>

                {loading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}

                {!loading && result?.no_bins && (
                    <Alert severity="warning">
                        Esta bodega no tiene posiciones estructuradas donde asignar. Genera un pasillo primero desde
                        el tablero de Posiciones.
                    </Alert>
                )}

                {!loading && result && !result.no_bins && (
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                            <Chip color="primary" label={`${result.products} productos a posicionar`} />
                            <Chip variant="outlined" label={`${result.bins_used} posiciones disponibles`} />
                            {result.skipped_stale > 0 && (
                                <Chip variant="outlined" color="default"
                                      label={`${result.skipped_stale} sin movimiento, omitidos`} />
                            )}
                        </Stack>

                        {result.skipped_stale > 0 && !includeStale && (
                            <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                                <Typography variant="body2">
                                    El stock que no se movió queda fuera a propósito: darle el mejor hueco de la
                                    bodega a algo que no sale hace meses se lo quita a un producto que sí se pickea.
                                </Typography>
                            </Alert>
                        )}

                        {suggestions.length > 0 && (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Código</TableCell>
                                        <TableCell>Producto</TableCell>
                                        <TableCell align="right">Rotación</TableCell>
                                        <TableCell>Posición sugerida</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {suggestions.slice(0, 50).map((row) => (
                                        <TableRow key={row.code} hover>
                                            <TableCell>{row.code}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                                                    {row.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                    <Typography variant="body2">{row.picks}</Typography>
                                                    <Chip size="small" variant="outlined" label={row.abc_class}
                                                          color={row.abc_class === "A" ? "primary" : "default"} />
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" color="success" variant="outlined" label={row.bin_code} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {suggestions.length > 50 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                Se muestran las primeras 50 de {suggestions.length}. Se aplican todas.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton variant="contained" loading={applying} disabled={!suggestions.length}
                               onClick={apply}>
                    Aplicar {suggestions.length > 0 ? `(${suggestions.length})` : ""}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default SlottingDialog;
